<template lang="pug">
#questionnaire-detail
  .box
    .box-header.register-header
      .title
        span アンケート結果の確認
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}
    .box-body
      b-tabs
        b-tab.tab(title="回答結果")
          QuestionnaireDetailTab(
            :isBusy.sync="isBusy" :questionnaireAndAnswers="questionnaireAndAnswers"
            :responsedUsers="responsedUsers" :nonresponsedUsers="nonresponsedUsers" :requestType="requestType"
            @onResendQuestionnaire="onResendQuestionnaire(_questionnaire)"
            @onResendQuestionnaireToAll="onResendQuestionnaireToAll(_questionnaire)")
        b-tab.tab(title="再送履歴")
          ResendHistoryListTab(:isBusy.sync="isBusy" :questionnaire="_questionnaire" :usersMap="usersMap")

    .box-footer.with-border.register-footer
      .buttons
        button.btn.cancel-btn(@click="gotoQuestionnaireList" type="button") 戻る

  PageLoading(:isBusy="isBusy" :message="loadingMessage")

  ResendInputModal(v-model="showResendInputModal" :modalTitle="resendInputModalTitle" :target="resendTargets" :messageTitle.sync="resendMessageTitle"
    :messageText.sync="resendMessageText" :showValidationAlert.sync="showValidationAlert" @ok="confirmResend")
  OkCancelModal(v-model="showConfirmResendModal" :title="confirmResendModalTitle" @ok="resendQuestionnaire(_questionnaire)")
  OkModal(v-model="showResendSuccessModal" title="送信を受け付けました")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import OtherUserGroup from "@/commons/models/OtherUserGroup";

import QuestionnairesService, {
  GroupType,
  QuestionnaireAnswersGetModel,
  QuestionnairesGetModel,
  TargetType,
  ResendQuestionnairesPostModel,
  SendMessageGroupInfo,
  ResidentGroupInfo,
  OtherUserGroupInfo
} from "@/commons/services/Questionnaires";
import FamiliesService, { FamilyGetModel, SenderType } from "@/commons/services/Families";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ResendInputModal from "@/pc/containers/questionnaires/ResendInputModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";

import QuestionnaireDetailTab from "@/pc/containers/questionnaires/QuestionnaireDetailTab.vue";
import ResendHistoryListTab from "@/pc/containers/questionnaires/ResendHistoryListTab.vue";

/**
 * ### アンケート登録画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    QuestionnaireDetailTab,
    ResendHistoryListTab,
    ResendInputModal,
    OkModal,
    OkCancelModal
  },
  $_veeValidate: { validator: "new" }
})
export default class QuestionnaireDetail extends Vue {
  /** アンケートサービス */
  @Inject("QuestionnairesService") questionnairesService!: QuestionnairesService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** 各タブローディング表示可否 */
  isBusy: boolean = false;
  /** ページ全体ローディング表示可否 */
  isLoading: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** アンケートID */
  id: number | null = null;
  /** アンケート、アンケート回答。子コンポーネントにまとめて渡すのでObjectにまとめる */
  questionnaireAndAnswers: { questionnaire: QuestionnairesGetModel; questionnaireAnswers: Array<QuestionnaireAnswersGetModel> } | null = null;
  /** リクエストの種類(家族or施設関係者) */
  requestType: string = "";
  /** ユーザーMAP */
  usersMap: Map<string, FamilyGetModel | OtherUserGroup> = new Map();
  /** 回答のユーザー(家族or施設関係者) */
  responsedUsers: Array<FamilyGetModel> | Array<OtherUserGroup> = [];
  /** 未回答のユーザー(家族or施設関係者) */
  nonresponsedUsers: Array<FamilyGetModel> | Array<OtherUserGroup> = [];

  /** 再送入力モーダル表示可否 */
  showResendInputModal: boolean = false;
  /** 再送入力モーダルタイトル */
  resendInputModalTitle: string = "";
  /** 再送入力モーダル宛先 */
  resendTargets: string = "";
  /** 再送入力モーダルメッセージタイトル */
  resendMessageTitle: string = "";
  /** 再送入力モーダルメッセージ本文 */
  resendMessageText: string = "";

  /** 再送確認モーダル表示可否 */
  showConfirmResendModal: boolean = false;
  /** 再送確認モーダル表示可否 */
  confirmResendModalTitle: string = "";

  /** 再送完了モーダル表示可否 */
  showResendSuccessModal: boolean = false;

  get _questionnaire(): QuestionnairesGetModel | null {
    if (!this.questionnaireAndAnswers || !this.questionnaireAndAnswers.questionnaire) {
      return null;
    } else {
      return this.questionnaireAndAnswers.questionnaire;
    }
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 期日の初期値を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const id: number = Number(this.$route.params.id);
    this.id = id;
    const questionnaire: QuestionnairesGetModel = await this.getQuestionnaire(id);
    const questionnaireAnswers: Array<QuestionnaireAnswersGetModel> = await this.getQuestionnaireAnswers(id);

    const requestType: string = this.getRequestType(questionnaire);
    this.requestType = requestType;

    if (requestType == "families") {
      const families: Array<FamilyGetModel> = await this.getFamiliesInFacility();
      const map: Map<string, FamilyGetModel> = this.createFamilyMap(families);
      this.usersMap = map;
      this.responsedUsers = this.createResponsedFamilies(questionnaireAnswers, map);
      this.nonresponsedUsers = this.createNonresponsedFamilies(questionnaire, questionnaireAnswers, map);
    } else if (requestType == "otherUserGroups") {
      const members: Array<OtherUserGroup> = await this.getOtherUserGroupMembersInFacility();
      const map: Map<string, OtherUserGroup> = this.createMemberMap(members);
      this.usersMap = map;
      this.responsedUsers = this.createResponsedMembers(questionnaireAnswers, map);
      this.nonresponsedUsers = this.createNonresponsedMembers(questionnaire, questionnaireAnswers, map);
    }

    // アンケートに関連する情報が格納されると子コンポーネントのtable要素が作られるので回答済・未回答の情報を格納後にアンケート情報を格納する
    this.questionnaireAndAnswers = {
      questionnaire,
      questionnaireAnswers
    };

    this.isBusy = false;

    return;
  }

  /**
   * ### アンケートを取得する
   */
  public async getQuestionnaire(id: number): Promise<QuestionnairesGetModel> {
    const response: QuestionnairesGetModel = await this.questionnairesService.get(userStore, id);
    return response;
  }

  /**
   * ### 回答を取得する
   */
  public async getQuestionnaireAnswers(id: number): Promise<Array<QuestionnaireAnswersGetModel>> {
    const response: Array<QuestionnaireAnswersGetModel> = await this.questionnairesService.getAnswers(userStore, id);
    return response;
  }

  /**
   * ### ユーザーが家族か施設関係者かを返す
   *
   * - 可読性のために文字列で返す
   */
  public getRequestType(questionnaire: QuestionnairesGetModel): string {
    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES:
      case TargetType.FAMILIES:
      case TargetType.RESIDENT_GROUPS: {
        return "families";
      }
      case TargetType.OTHER_USERS:
      case TargetType.OTHER_USER_GROUPS: {
        return "otherUserGroups";
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        for (const groupInfo of questionnaire.sendMessageGroupInfos) {
          if (groupInfo.type == GroupType.FAMILY) {
            return "families";
          } else {
            return "otherUserGroups";
          }
        }
        return "";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 施設にひもづく全家族を取得する
   */
  public async getFamiliesInFacility(): Promise<Array<FamilyGetModel>> {
    const response: Array<FamilyGetModel> = await this.familiesService.getsInFacility(userStore, { addDeleted: "true" });
    return response;
  }

  /**
   * ### 家族MAPを作成する
   *
   * - ソートキーをkeyとし、valueをレコードとする
   */
  public createFamilyMap(families: Array<FamilyGetModel>): Map<string, FamilyGetModel> {
    const map: Map<string, FamilyGetModel> = new Map<string, FamilyGetModel>();
    for (const family of families) {
      map.set(family.residentsSortKey, family);
    }
    return map;
  }

  /**
   * ### 回答のユーザーを格納する(家族)
   */
  public createResponsedFamilies(questionnaireAnswers: Array<QuestionnaireAnswersGetModel>, map: Map<string, FamilyGetModel>): Array<FamilyGetModel> {
    const responsedUsers: Array<FamilyGetModel> = [];
    for (const answer of questionnaireAnswers) {
      const family: FamilyGetModel | undefined = map.get(answer.targetSortKey);
      if (family) {
        responsedUsers.push(family);
      }
    }
    return responsedUsers;
  }

  /**
   * ### 未回答のユーザーを格納する(家族)
   */
  public createNonresponsedFamilies(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswers: Array<QuestionnaireAnswersGetModel>,
    map: Map<string, FamilyGetModel>
  ): Array<FamilyGetModel> {
    const responsedUserMap: Map<string, string> = new Map<string, string>();
    for (const answer of questionnaireAnswers) {
      responsedUserMap.set(answer.targetSortKey, answer.targetSortKey);
    }

    const nonresponsedUsers: Array<FamilyGetModel> = [];
    for (const target of questionnaire.targets) {
      const responsedUser: string | undefined = responsedUserMap.get(target);
      if (responsedUser) {
        continue;
      } else {
        const family: FamilyGetModel | undefined = map.get(target);
        if (family) {
          nonresponsedUsers.push(family);
        }
      }
    }
    return nonresponsedUsers;
  }

  /**
   * ### 施設にひもづく全施設関係者を取得する
   */
  public async getOtherUserGroupMembersInFacility(): Promise<Array<OtherUserGroup>> {
    const response: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore);
    return response;
  }

  /**
   * ### 施設関係者グループメンバーMAPを作成する
   *
   * - ソートキーをkeyとし、valueをレコードとする
   */
  public createMemberMap(members: Array<OtherUserGroup>): Map<string, OtherUserGroup> {
    const map: Map<string, OtherUserGroup> = new Map<string, OtherUserGroup>();
    for (const member of members) {
      map.set(member.otherUserGroupsSortKey, member);
    }
    return map;
  }

  /**
   * ### 回答のユーザーを格納する(施設関係者)
   */
  public createResponsedMembers(questionnaireAnswers: Array<QuestionnaireAnswersGetModel>, map: Map<string, OtherUserGroup>): Array<OtherUserGroup> {
    const responsedUsers: Array<OtherUserGroup> = [];
    for (const answer of questionnaireAnswers) {
      const member: OtherUserGroup | undefined = map.get(answer.targetSortKey);
      if (member) {
        responsedUsers.push(member);
      }
    }
    return responsedUsers;
  }

  /**
   * ### 未回答のユーザーを格納する(施設関係者)
   */
  public createNonresponsedMembers(
    questionnaire: QuestionnairesGetModel,
    questionnaireAnswers: Array<QuestionnaireAnswersGetModel>,
    map: Map<string, OtherUserGroup>
  ): Array<OtherUserGroup> {
    const responsedUserMap: Map<string, string> = new Map<string, string>();
    for (const answer of questionnaireAnswers) {
      responsedUserMap.set(answer.targetSortKey, answer.targetSortKey);
    }

    const nonresponsedUsers: Array<OtherUserGroup> = [];
    for (const target of questionnaire.targets) {
      const responsedUser: string | undefined = responsedUserMap.get(target);
      if (responsedUser) {
        continue;
      } else {
        const member: OtherUserGroup | undefined = map.get(target);
        if (member) {
          nonresponsedUsers.push(member);
        }
      }
    }
    return nonresponsedUsers;
  }

  /**
   * ### アンケート再送の入力モーダルを表示する
   */
  public onResendQuestionnaire(questionnaire: QuestionnairesGetModel): void {
    this.resendTargets = this.createResendTarget();
    this.resendInputModalTitle = "未回答者に再送";
    this.resendMessageTitle = questionnaire.messageTitle;
    this.resendMessageText = questionnaire.messageText;
    this.showResendInputModal = true;
    return;
  }

  public createResendTarget(): string {
    const targets: string[] = [];
    if (this.requestType == "families") {
      const nonresponsedUsers: Array<FamilyGetModel> = this.nonresponsedUsers as Array<FamilyGetModel>;
      for (const user of nonresponsedUsers) {
        targets.push(user.familyName);
      }
    } else if (this.requestType == "otherUserGroups") {
      const nonresponsedUsers: Array<OtherUserGroup> = this.nonresponsedUsers as Array<OtherUserGroup>;
      for (const user of nonresponsedUsers) {
        targets.push(user.memberName);
      }
    }
    return targets.join("、");
  }

  /**
   * ### アンケート再送の入力モーダルを表示する
   */
  public onResendQuestionnaireToAll(questionnaire: QuestionnairesGetModel): void {
    this.resendTargets = this.createResendTargetToAll();
    this.resendInputModalTitle = "全員に再送";
    this.resendMessageTitle = questionnaire.messageTitle;
    this.resendMessageText = questionnaire.messageText;
    this.showResendInputModal = true;
    return;
  }

  public createResendTargetToAll(): string {
    const targets: string[] = [];
    if (this.requestType == "families") {
      const nonresponsedUsers: Array<FamilyGetModel> = this.nonresponsedUsers as Array<FamilyGetModel>;
      for (const user of nonresponsedUsers) {
        targets.push(user.familyName);
      }
      const responsedUsers: Array<FamilyGetModel> = this.responsedUsers as Array<FamilyGetModel>;
      for (const user of responsedUsers) {
        targets.push(user.familyName);
      }
    } else if (this.requestType == "otherUserGroups") {
      const nonresponsedUsers: Array<OtherUserGroup> = this.nonresponsedUsers as Array<OtherUserGroup>;
      for (const user of nonresponsedUsers) {
        targets.push(user.memberName);
      }
      const responsedUsers: Array<OtherUserGroup> = this.responsedUsers as Array<OtherUserGroup>;
      for (const user of responsedUsers) {
        targets.push(user.memberName);
      }
    }
    return targets.join("、");
  }

  /**
   * ### アンケート再送の確認モーダルを表示する
   */
  public async confirmResend(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      const questionnaire: QuestionnairesGetModel = this.questionnaireAndAnswers!.questionnaire as QuestionnairesGetModel;
      if (!questionnaire.hidden) {
        this.confirmResendModalTitle = "未回答者に再送します。よろしいですか？";
      } else {
        this.confirmResendModalTitle = "全員に再送します。よろしいですか？";
      }
      this.showConfirmResendModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### アンケート再送
   */
  public async resendQuestionnaire(questionnaire: QuestionnairesGetModel): Promise<void> {
    this.showResendInputModal = false;
    this.loadingMessage = "送信中...";
    this.isBusy = true;

    try {
      let requestBody: ResendQuestionnairesPostModel | null = null;
      if (!questionnaire.hidden) {
        requestBody = this.createResendRequestBody(questionnaire);
      } else {
        requestBody = this.createResendRequestBodyToAll(questionnaire);
      }
      await this.questionnairesService.reSend(userStore, Number(this.id), requestBody);
      this.showResendSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    }

    // APIのデータ登録を待つので5秒待機してから画面を再読み込みする
    this.loadingMessage = "読込み中...";
    await this.$timerUtil.setTimeout(5000);
    await this.createView();

    return;
  }

  public createResendRequestBody(questionnaire: QuestionnairesGetModel): ResendQuestionnairesPostModel {
    const targets: string[] = [];
    const lineTargets: string[] = [];
    const mailTargets: string[] = [];
    const sendMessageGroupInfos: Array<SendMessageGroupInfo> = [];
    const residentGroupInfos: Array<ResidentGroupInfo> = [];
    const otherUserGroupInfos: Array<OtherUserGroupInfo> = [];

    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES:
      case TargetType.FAMILIES: {
        const nonresponsedUsers: Array<FamilyGetModel> = this.nonresponsedUsers as Array<FamilyGetModel>;
        for (const user of nonresponsedUsers) {
          targets.push(user.residentsSortKey);

          if (user.senderType == SenderType.LINE) {
            lineTargets.push(user.residentsSortKey);
          } else if (user.senderType == SenderType.MAIL) {
            mailTargets.push(user.residentsSortKey);
          }
        }
        break;
      }
      case TargetType.OTHER_USERS: {
        const nonresponsedUsers: Array<OtherUserGroup> = this.nonresponsedUsers as Array<OtherUserGroup>;
        for (const user of nonresponsedUsers) {
          targets.push(user.otherUserGroupsSortKey);

          if (user.senderType == SenderType.LINE) {
            lineTargets.push(user.otherUserGroupsSortKey);
          } else if (user.senderType == SenderType.MAIL) {
            mailTargets.push(user.otherUserGroupsSortKey);
          }
        }
        break;
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        const nonresponsedUsersMap: Map<string, FamilyGetModel | OtherUserGroup> = new Map();
        for (const user of this.nonresponsedUsers) {
          if (this.requestType == "families") {
            const _user: FamilyGetModel = user as FamilyGetModel;
            nonresponsedUsersMap.set(_user.residentsSortKey, user);
          } else {
            const _user: OtherUserGroup = user as OtherUserGroup;
            nonresponsedUsersMap.set(_user.otherUserGroupsSortKey, user);
          }
        }

        for (const groupInfo of questionnaire.sendMessageGroupInfos) {
          const memberSortKeys: string[] = [];
          for (const target of groupInfo.memberSortKeys) {
            const user: FamilyGetModel | OtherUserGroup | undefined = nonresponsedUsersMap.get(target);
            if (user) {
              targets.push(target);

              if (user.senderType == SenderType.LINE) {
                lineTargets.push(target);
              } else if (user.senderType == SenderType.MAIL) {
                mailTargets.push(target);
              }

              memberSortKeys.push(target);
            }
          }
          if (0 < memberSortKeys.length) {
            sendMessageGroupInfos.push({
              ...groupInfo,
              memberSortKeys: memberSortKeys
            });
          }
        }
        break;
      }
      case TargetType.RESIDENT_GROUPS: {
        const nonresponsedUsersMap: Map<string, FamilyGetModel> = new Map();
        for (const user of this.nonresponsedUsers) {
          const _user: FamilyGetModel = user as FamilyGetModel;
          nonresponsedUsersMap.set(_user.residentsSortKey, _user);
        }

        for (const groupInfo of questionnaire.residentGroupInfos) {
          const residentsSortKeys: string[] = [];
          for (const target of groupInfo.residentsSortKeys) {
            const user: FamilyGetModel | OtherUserGroup | undefined = nonresponsedUsersMap.get(target);
            if (user) {
              targets.push(target);

              if (user.senderType == SenderType.LINE) {
                lineTargets.push(target);
              } else if (user.senderType == SenderType.MAIL) {
                mailTargets.push(target);
              }

              residentsSortKeys.push(target);
            }
          }
          if (0 < residentsSortKeys.length) {
            residentGroupInfos.push({
              ...groupInfo,
              residentsSortKeys: residentsSortKeys
            });
          }
        }
        break;
      }
      case TargetType.OTHER_USER_GROUPS: {
        const nonresponsedUsersMap: Map<string, OtherUserGroup> = new Map();
        for (const user of this.nonresponsedUsers) {
          const _user: OtherUserGroup = user as OtherUserGroup;
          nonresponsedUsersMap.set(_user.otherUserGroupsSortKey, _user);
        }

        for (const groupInfo of questionnaire.otherUserGroupInfos) {
          const memberSortKeys: string[] = [];
          for (const target of groupInfo.memberSortKeys) {
            const user: OtherUserGroup | undefined = nonresponsedUsersMap.get(target);
            if (user) {
              targets.push(target);

              if (user.senderType == SenderType.LINE) {
                lineTargets.push(target);
              } else if (user.senderType == SenderType.MAIL) {
                mailTargets.push(target);
              }

              memberSortKeys.push(target);
            }
          }
          if (0 < memberSortKeys.length) {
            otherUserGroupInfos.push({
              ...groupInfo,
              memberSortKeys: memberSortKeys
            });
          }
        }
        break;
      }
    }

    return {
      messageTitle: this.resendMessageTitle,
      messageText: this.resendMessageText,
      targets: targets,
      lineTargets: lineTargets,
      mailTargets: mailTargets,
      sendMessageGroupInfos: sendMessageGroupInfos,
      residentGroupInfos: residentGroupInfos,
      otherUserGroupInfos: otherUserGroupInfos,
      updated: questionnaire.updated
    };
  }

  public createResendRequestBodyToAll(questionnaire: QuestionnairesGetModel): ResendQuestionnairesPostModel {
    const targets: string[] = [];
    const lineTargets: string[] = [];
    const mailTargets: string[] = [];
    const sendMessageGroupInfos: Array<SendMessageGroupInfo> = [];
    const residentGroupInfos: Array<ResidentGroupInfo> = [];
    const otherUserGroupInfos: Array<OtherUserGroupInfo> = [];

    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES:
      case TargetType.FAMILIES: {
        const nonresponsedUsers: Array<FamilyGetModel> = this.nonresponsedUsers as Array<FamilyGetModel>;
        for (const user of nonresponsedUsers) {
          targets.push(user.residentsSortKey);

          if (user.senderType == SenderType.LINE) {
            lineTargets.push(user.residentsSortKey);
          } else if (user.senderType == SenderType.MAIL) {
            mailTargets.push(user.residentsSortKey);
          }
        }
        const responsedUsers: Array<FamilyGetModel> = this.responsedUsers as Array<FamilyGetModel>;
        for (const user of responsedUsers) {
          targets.push(user.residentsSortKey);

          if (user.senderType == SenderType.LINE) {
            lineTargets.push(user.residentsSortKey);
          } else if (user.senderType == SenderType.MAIL) {
            mailTargets.push(user.residentsSortKey);
          }
        }
        break;
      }
      case TargetType.OTHER_USERS: {
        const nonresponsedUsers: Array<OtherUserGroup> = this.nonresponsedUsers as Array<OtherUserGroup>;
        for (const user of nonresponsedUsers) {
          targets.push(user.otherUserGroupsSortKey);

          if (user.senderType == SenderType.LINE) {
            lineTargets.push(user.otherUserGroupsSortKey);
          } else if (user.senderType == SenderType.MAIL) {
            mailTargets.push(user.otherUserGroupsSortKey);
          }
        }
        const responsedUsers: Array<OtherUserGroup> = this.responsedUsers as Array<OtherUserGroup>;
        for (const user of responsedUsers) {
          targets.push(user.otherUserGroupsSortKey);

          if (user.senderType == SenderType.LINE) {
            lineTargets.push(user.otherUserGroupsSortKey);
          } else if (user.senderType == SenderType.MAIL) {
            mailTargets.push(user.otherUserGroupsSortKey);
          }
        }
        break;
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        const usersMap: Map<string, FamilyGetModel | OtherUserGroup> = new Map();
        for (const user of this.nonresponsedUsers) {
          if (this.requestType == "families") {
            const _user: FamilyGetModel = user as FamilyGetModel;
            usersMap.set(_user.residentsSortKey, user);
          } else {
            const _user: OtherUserGroup = user as OtherUserGroup;
            usersMap.set(_user.otherUserGroupsSortKey, user);
          }
        }
        for (const user of this.responsedUsers) {
          if (this.requestType == "families") {
            const _user: FamilyGetModel = user as FamilyGetModel;
            usersMap.set(_user.residentsSortKey, user);
          } else {
            const _user: OtherUserGroup = user as OtherUserGroup;
            usersMap.set(_user.otherUserGroupsSortKey, user);
          }
        }

        for (const groupInfo of questionnaire.sendMessageGroupInfos) {
          const memberSortKeys: string[] = [];
          for (const target of groupInfo.memberSortKeys) {
            const user: FamilyGetModel | OtherUserGroup | undefined = usersMap.get(target);
            if (user) {
              targets.push(target);

              if (user.senderType == SenderType.LINE) {
                lineTargets.push(target);
              } else if (user.senderType == SenderType.MAIL) {
                mailTargets.push(target);
              }

              memberSortKeys.push(target);
            }
          }
          if (0 < memberSortKeys.length) {
            sendMessageGroupInfos.push({
              ...groupInfo,
              memberSortKeys: memberSortKeys
            });
          }
        }
        break;
      }
      case TargetType.RESIDENT_GROUPS: {
        const usersMap: Map<string, FamilyGetModel> = new Map();
        for (const user of this.nonresponsedUsers) {
          const _user: FamilyGetModel = user as FamilyGetModel;
          usersMap.set(_user.residentsSortKey, _user);
        }
        for (const user of this.responsedUsers) {
          const _user: FamilyGetModel = user as FamilyGetModel;
          usersMap.set(_user.residentsSortKey, _user);
        }

        for (const groupInfo of questionnaire.residentGroupInfos) {
          const residentsSortKeys: string[] = [];
          for (const target of groupInfo.residentsSortKeys) {
            const user: FamilyGetModel | undefined = usersMap.get(target);
            if (user) {
              targets.push(target);

              if (user.senderType == SenderType.LINE) {
                lineTargets.push(target);
              } else if (user.senderType == SenderType.MAIL) {
                mailTargets.push(target);
              }

              residentsSortKeys.push(target);
            }
          }
          if (0 < residentsSortKeys.length) {
            residentGroupInfos.push({
              ...groupInfo,
              residentsSortKeys: residentsSortKeys
            });
          }
        }
        break;
      }
      case TargetType.OTHER_USER_GROUPS: {
        const usersMap: Map<string, OtherUserGroup> = new Map();
        for (const user of this.nonresponsedUsers) {
          const _user: OtherUserGroup = user as OtherUserGroup;
          usersMap.set(_user.otherUserGroupsSortKey, _user);
        }
        for (const user of this.responsedUsers) {
          const _user: OtherUserGroup = user as OtherUserGroup;
          usersMap.set(_user.otherUserGroupsSortKey, _user);
        }

        for (const groupInfo of questionnaire.otherUserGroupInfos) {
          const memberSortKeys: string[] = [];
          for (const target of groupInfo.memberSortKeys) {
            const user: OtherUserGroup | undefined = usersMap.get(target);
            if (user) {
              targets.push(target);

              if (user.senderType == SenderType.LINE) {
                lineTargets.push(target);
              } else if (user.senderType == SenderType.MAIL) {
                mailTargets.push(target);
              }

              memberSortKeys.push(target);
            }
          }
          if (0 < memberSortKeys.length) {
            otherUserGroupInfos.push({
              ...groupInfo,
              memberSortKeys: memberSortKeys
            });
          }
        }
        break;
      }
    }

    return {
      messageTitle: this.resendMessageTitle,
      messageText: this.resendMessageText,
      targets: targets,
      lineTargets: lineTargets,
      mailTargets: mailTargets,
      sendMessageGroupInfos: sendMessageGroupInfos,
      residentGroupInfos: residentGroupInfos,
      otherUserGroupInfos: otherUserGroupInfos,
      updated: questionnaire.updated
    };
  }

  /**
   * ### アンケート一覧画面へ遷移する
   */
  public gotoQuestionnaireList(): void {
    this.$router.push(PcUrl.QuestionnaireList);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#questionnaire-detail {
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
  .cancel-btn {
    width: 120px;
  }
}
</style>
